import React, { useCallback, useEffect, useMemo, useState } from "react";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  useMediaQuery,
} from "@mui/material";
import {
  Button as RaButton,
  FieldTitle,
  useListContext,
  useNotify,
  useResourceContext,
  useTranslate,
} from "react-admin";

import {
  createSavedFilter,
  deleteSavedFilter,
  getSavedFilters,
  renameSavedFilter,
  SavedFilter,
  SavedFilterScope,
} from "../api/saved_filters";

type FilterProps = {
  alwaysOn?: boolean;
  choices?: { id: unknown; name: React.ReactNode }[];
  label?: string | React.ReactElement | boolean;
  source: string;
};

type DialogState =
  | { type: "create" }
  | { type: "rename"; savedFilter: SavedFilter }
  | { type: "delete"; savedFilter: SavedFilter }
  | null;

type NameDialogState = Extract<DialogState, { type: "create" | "rename" }>;
type DeleteDialogState = Extract<DialogState, { type: "delete" }>;

const SAVED_FILTER_NAME_MAX_LENGTH = 50;
const MAX_SAVED_FILTERS_PER_SCOPE = 10;

const getFilterValueLabel = (
  filter: React.ReactElement<FilterProps>,
  value: unknown,
  translate: ReturnType<typeof useTranslate>
) => {
  const choice = filter.props.choices?.find((item) => item.id === value);
  if (!choice) {
    return String(value);
  }
  return typeof choice.name === "string" ? translate(choice.name, { _: choice.name }) : choice.name;
};

const SavedFilterSummary = ({
  filters,
  values,
}: {
  filters: React.ReactElement<FilterProps>[];
  values: Record<string, unknown>;
}) => {
  const resource = useResourceContext();
  const translate = useTranslate();
  const filterValues = filters.filter((filter) => Object.prototype.hasOwnProperty.call(values, filter.props.source));

  if (filterValues.length === 0) {
    return <Box>条件なし</Box>;
  }

  return (
    <Box sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
      {filterValues.map((filter, index) => (
        <React.Fragment key={filter.props.source}>
          {index > 0 && " / "}
          <Box component="span">
            <FieldTitle label={filter.props.label} resource={resource} source={filter.props.source} />
          </Box>
          <Box component="span">：{getFilterValueLabel(filter, values[filter.props.source], translate)}</Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

type SavedFilterMenuItemsProps = {
  isLoading: boolean;
  savedFilters: SavedFilter[];
  onApply: (savedFilter: SavedFilter) => void;
  onRename: (savedFilter: SavedFilter) => void;
  onDelete: (savedFilter: SavedFilter) => void;
};

type SavedFilterMenuItemProps = {
  savedFilter: SavedFilter;
  onApply: (savedFilter: SavedFilter) => void;
  onRename: (savedFilter: SavedFilter) => void;
  onDelete: (savedFilter: SavedFilter) => void;
};

const SavedFilterMenuItem = ({ savedFilter, onApply, onRename, onDelete }: SavedFilterMenuItemProps) => (
  <MenuItem sx={{ alignItems: "flex-start" }} onClick={() => onApply(savedFilter)}>
    <ListItemText
      primary={savedFilter.name}
      primaryTypographyProps={{ sx: { overflowWrap: "anywhere", whiteSpace: "normal" } }}
      sx={{ flex: 1, minWidth: 0 }}
    />
    <Button
      color="inherit"
      size="small"
      startIcon={<EditIcon fontSize="small" />}
      onClick={(event) => {
        event.stopPropagation();
        onRename(savedFilter);
      }}
    >
      名称変更
    </Button>
    <Button
      color="error"
      size="small"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={(event) => {
        event.stopPropagation();
        onDelete(savedFilter);
      }}
    >
      削除
    </Button>
  </MenuItem>
);

const SavedFilterMenuItems = ({ isLoading, savedFilters, onApply, onRename, onDelete }: SavedFilterMenuItemsProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (savedFilters.length === 0) {
    return <MenuItem disabled>保存した検索条件はありません</MenuItem>;
  }

  return (
    <>
      {savedFilters.map((savedFilter) => (
        <SavedFilterMenuItem
          key={savedFilter.id}
          savedFilter={savedFilter}
          onApply={onApply}
          onDelete={onDelete}
          onRename={onRename}
        />
      ))}
    </>
  );
};

type SavedFilterNameDialogProps = {
  dialog: NameDialogState | null;
  filters: React.ReactElement<FilterProps>[];
  scope: SavedFilterScope;
  values: Record<string, unknown>;
  onChanged: () => Promise<void>;
  onClose: () => void;
};

const SavedFilterNameDialog = ({ dialog, filters, scope, values, onChanged, onClose }: SavedFilterNameDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const summaryValues = dialog?.type === "rename" ? dialog.savedFilter.filters : values;
  const trimmedName = name.trim();
  const isNameTooLong = Array.from(trimmedName).length > SAVED_FILTER_NAME_MAX_LENGTH;

  useEffect(() => {
    setName(dialog?.type === "rename" ? dialog.savedFilter.name : "");
  }, [dialog]);

  const handleSubmit = async () => {
    if (!dialog || trimmedName === "" || isNameTooLong) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (dialog.type === "create") {
        await createSavedFilter({ scope, name: trimmedName, filters: values });
      } else {
        await renameSavedFilter(dialog.savedFilter.id, trimmedName);
      }
      await onChanged();
      notify(dialog.type === "create" ? "検索条件を保存しました" : "検索条件の名称を変更しました", {
        type: "info",
      });
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : "検索条件の保存に失敗しました", { type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // React 18 StrictMode で TextField の autoFocus が失われる MUI の既知事象。
  // TODO: React または MUI 更新時に issue の解消状況と StrictMode での autoFocus を確認し、解消済みなら削除する。
  // https://github.com/mui/material-ui/issues/33330
  return (
    <Dialog
      disableRestoreFocus
      fullWidth
      maxWidth="sm"
      open={dialog !== null}
      onClose={isSubmitting ? undefined : onClose}
    >
      <DialogTitle>
        {dialog?.type === "create" ? "現在の検索条件を保存" : "条件名を変更"}
        <IconButton
          aria-label="閉じる"
          disabled={isSubmitting}
          size="small"
          sx={{ position: "absolute", right: 16, top: 16 }}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          error={isNameTooLong}
          fullWidth
          helperText={isNameTooLong && `条件名は${SAVED_FILTER_NAME_MAX_LENGTH}文字以内で入力してください`}
          label="条件名"
          margin="dense"
          placeholder="例: 引取可能日が近い条件"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Box sx={{ backgroundColor: "action.hover", mt: 2, p: 1.25 }}>
          <Box sx={{ color: "text.secondary", mb: 0.25 }}>検索条件</Box>
          <SavedFilterSummary filters={filters} values={summaryValues} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button disabled={isSubmitting} onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button disabled={isSubmitting || trimmedName === "" || isNameTooLong} onClick={handleSubmit}>
          {translate("ra.action.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type SavedFilterDeleteDialogProps = {
  dialog: DeleteDialogState | null;
  filters: React.ReactElement<FilterProps>[];
  onChanged: () => Promise<void>;
  onClose: () => void;
};

const SavedFilterDeleteDialog = ({ dialog, filters, onChanged, onClose }: SavedFilterDeleteDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!dialog) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteSavedFilter(dialog.savedFilter.id);
      await onChanged();
      notify("保存条件を削除しました", { type: "info" });
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : "検索条件の削除に失敗しました", { type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={dialog !== null} onClose={isSubmitting ? undefined : onClose}>
      <DialogTitle>
        保存条件を削除
        <IconButton
          aria-label="閉じる"
          disabled={isSubmitting}
          size="small"
          sx={{ position: "absolute", right: 16, top: 16 }}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ color: "text.secondary", mb: 2 }}>削除しますか？</Box>
        {dialog && (
          <>
            <Box sx={{ backgroundColor: "action.hover", p: 1.25 }}>
              <Box sx={{ color: "text.secondary", mb: 0.25 }}>条件名</Box>
              <Box sx={{ overflowWrap: "anywhere" }}>{dialog.savedFilter.name}</Box>
            </Box>
            <Box sx={{ backgroundColor: "action.hover", mt: 2, p: 1.25 }}>
              <Box sx={{ color: "text.secondary", mb: 0.25 }}>検索条件</Box>
              <SavedFilterSummary filters={filters} values={dialog.savedFilter.filters} />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button disabled={isSubmitting} onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button color="error" disabled={isSubmitting} onClick={handleDelete}>
          {translate("ra.action.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getDisplayedFilters = (
  filterElements: React.ReactElement<FilterProps>[],
  filterValues: Record<string, unknown>
) =>
  filterElements.reduce((displayedFilters, filter) => {
    if (filter.props.alwaysOn || Object.prototype.hasOwnProperty.call(filterValues, filter.props.source)) {
      displayedFilters[filter.props.source] = true;
    }
    return displayedFilters;
  }, {} as Record<string, boolean>);

const getActiveFilterValues = (filterValues: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(filterValues).filter(([, value]) => typeof value !== "undefined"));

export const SavedFiltersMenu = ({
  filters,
  scope,
}: {
  filters: React.ReactElement<FilterProps>[];
  scope: SavedFilterScope;
}) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const { filterValues, setFilters } = useListContext();
  const notify = useNotify();
  const filterElements = filters;
  const currentFilterValues = useMemo(() => getActiveFilterValues(filterValues), [filterValues]);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const hasFilterValues = Object.keys(currentFilterValues).length > 0;
  const hasReachedSavedFilterLimit = savedFilters.length >= MAX_SAVED_FILTERS_PER_SCOPE;

  const loadSavedFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      setSavedFilters(await getSavedFilters(scope));
    } catch (error) {
      notify(error instanceof Error ? error.message : "検索条件の取得に失敗しました", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [notify, scope]);

  useEffect(() => {
    if (anchorElement) {
      loadSavedFilters();
    }
  }, [anchorElement, loadSavedFilters]);

  if (isMobile) {
    return null;
  }

  const closeMenu = () => setAnchorElement(null);
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsLoading(true);
    setAnchorElement(event.currentTarget);
  };
  const closeDialog = () => {
    setDialog(null);
  };
  const openNameDialog = (nextDialog: NameDialogState) => {
    closeMenu();
    setDialog(nextDialog);
  };
  const openDeleteDialog = (savedFilter: SavedFilter) => {
    closeMenu();
    setDialog({ type: "delete", savedFilter });
  };
  const applySavedFilter = (savedFilter: SavedFilter) => {
    setFilters({ ...savedFilter.filters }, getDisplayedFilters(filterElements, savedFilter.filters), false);
    closeMenu();
  };
  const nameDialog: NameDialogState | null = dialog?.type === "create" || dialog?.type === "rename" ? dialog : null;
  const deleteDialog: DeleteDialogState | null = dialog?.type === "delete" ? dialog : null;

  return (
    <>
      <RaButton
        aria-controls={anchorElement ? "saved-filters-menu" : undefined}
        aria-haspopup="true"
        label="検索条件を保存"
        onClick={openMenu}
      >
        <BookmarkBorderIcon />
      </RaButton>
      <Menu
        anchorEl={anchorElement}
        id="saved-filters-menu"
        open={Boolean(anchorElement)}
        slotProps={{ paper: { sx: { maxWidth: "calc(100vw - 32px)", width: 360 } } }}
        onClose={closeMenu}
      >
        <MenuItem
          disabled={!hasFilterValues || isLoading || hasReachedSavedFilterLimit}
          onClick={() => openNameDialog({ type: "create" })}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          {hasReachedSavedFilterLimit ? "検索条件の保存は10件まで" : "現在の検索条件を保存"}
        </MenuItem>
        <Divider />
        <SavedFilterMenuItems
          isLoading={isLoading}
          savedFilters={savedFilters}
          onApply={applySavedFilter}
          onDelete={openDeleteDialog}
          onRename={(savedFilter) => openNameDialog({ type: "rename", savedFilter })}
        />
      </Menu>
      <SavedFilterNameDialog
        dialog={nameDialog}
        filters={filterElements}
        scope={scope}
        values={currentFilterValues}
        onChanged={loadSavedFilters}
        onClose={closeDialog}
      />
      <SavedFilterDeleteDialog
        dialog={deleteDialog}
        filters={filterElements}
        onChanged={loadSavedFilters}
        onClose={closeDialog}
      />
    </>
  );
};
